<template>
    <div class="content">
        <div class="container-fluid">
            <div class="row mt-3">
                <div class="col-12">
                    <card class="strpied-tabled-with-hover"
                          body-classes="table-full-width table-responsive"
                    >
                        <template slot="header">
                            <b-row>
                                <b-col lg="2" class="mt-4">
                                    <b-button class="btn-primary" @click="toggleFilters">{{ showFilters ? 'Hide Filters' : 'Show Filters' }}</b-button>
                                </b-col>
                                <b-col lg="1" class="mt-4">
                                    <b-button-toolbar key-nav aria-label="Toolbar with button groups">
                                        <b-button-group class="mx-1">
                                            <b-button style="padding: 5px 6px !important;" @click="exportToExcel()" :title="translate('exportToExcel')">
                                                <b-icon icon="file-earmark-excel-fill"></b-icon>
                                            </b-button>
                                            <b-button style="padding: 5px 6px !important;" @click="exportToPDF()" :title="translate('exportToPdf')">
                                                <b-icon icon="file-earmark-pdf-fill"></b-icon>
                                            </b-button>
                                            <b-button style="padding: 5px 6px !important;" @click="refreshData()" :title="translate('refresh')">
                                                <b-icon icon="arrow-repeat"></b-icon>
                                            </b-button>
                                        </b-button-group>
                                    </b-button-toolbar>
                                </b-col>
                            </b-row>

                            <!-- Filters Section -->
                            <b-collapse v-model="showFilters">
                                <b-row class="mt-3">
                                    <b-col lg="3">
                                        <label class="some-label">{{ translate('username') }}</label>
                                        <b-form-input 
                                            v-model="filters.username" 
                                            :placeholder="translate('enterUsername')"
                                            @keyup.enter="applyFilters">
                                        </b-form-input>
                                    </b-col>
                                    <b-col lg="2">
                                        <label class="some-label">{{ translate('action') }}</label>
                                        <b-form-select 
                                            v-model="filters.action" 
                                            :options="actionOptions"
                                            @change="applyFilters">
                                        </b-form-select>
                                    </b-col>
                                    <b-col lg="2">
                                        <label class="some-label">{{ translate('tableName') }}</label>
                                        <b-form-select
                                            v-model="filters.tableName"
                                            :options="tableNameOptions"
                                            @change="applyFilters"
                                        />
                                    </b-col>
                                    <b-col lg="2">
                                        <label class="some-label">Record ID</label>
                                        <b-form-input 
                                            v-model="filters.tableId" 
                                            placeholder="Enter Record ID"
                                            @keyup.enter="applyFilters">
                                        </b-form-input>
                                    </b-col>
                                </b-row>
                                <b-row class="mt-3">
                                    <b-col lg="3">
                                        <label class="some-label">{{ translate('fromDate') }}</label>
                                        <b-form-input 
                                            type="datetime-local"
                                            v-model="filters.fromDate">
                                        </b-form-input>
                                    </b-col>
                                    <b-col lg="3">
                                        <label class="some-label">{{ translate('toDate') }}</label>
                                        <b-form-input 
                                            type="datetime-local"
                                            v-model="filters.toDate">
                                        </b-form-input>
                                    </b-col>
                                    <b-col lg="3">
                                        <label class="some-label">Performed By</label>
                                        <b-form-select
                                            v-model="filters.userId"
                                            :options="userOptions"
                                            @change="applyFilters"
                                        />
                                    </b-col>
                                    <b-col lg="3">
                                        <label class="some-label">Company</label>
                                        <b-form-select
                                            v-model="filters.superUserId"
                                            :options="superAdminOptions"
                                            @change="applyFilters"
                                        />
                                    </b-col>
                                </b-row>
                                <b-row class="mt-3">
                                    <b-col lg="2">
                                        <b-button class="btn-secondary" @click="applyFilters">{{ translate('find') }}</b-button>
                                    </b-col>
                                    <b-col lg="2">
                                        <b-button class="btn-outline-secondary" @click="clearFilters">{{ translate('clear') }}</b-button>
                                    </b-col>
                                </b-row>
                            </b-collapse>
                        </template>

                        <!-- Table Container -->
                        <div class="table-container">
                            <b-table-simple
                                :small="true"
                                sticky-header
                                style="max-height: 395px;"
                                striped
                                responsive
                                hover
                            >
                                <b-thead class="sticky-header">
                                    <b-tr>
                                        <b-th>{{ translate('no') }}</b-th>
                                        <b-th>{{ translate('username') }}</b-th>
                                        <b-th>{{ translate('userFullName') }}</b-th>
                                        <b-th>{{ translate('action') }}</b-th>
                                        <b-th>{{ translate('tableName') }}</b-th>
                                        <b-th>Record ID</b-th>
                                        <b-th>{{ translate('description') }}</b-th>
                                        <b-th>{{ translate('ipAddress') }}</b-th>
                                        <b-th>{{ translate('createdAt') }}</b-th>
                                        <b-th>{{ translate('actions') }}</b-th>
                                    </b-tr>
                                </b-thead>
                                
                                <template v-if="spinner">
                                    <b-tbody class="text-center">
                                        <b-tr>
                                            <b-td colspan="10" class="text-center">
                                                <b-spinner style="height: 3rem; width: 3rem;" variant="warning"></b-spinner>
                                            </b-td>
                                        </b-tr>
                                    </b-tbody>
                                </template>
                                
                                <template v-else>
                                    <template v-if="logs.length === 0">
                                        <b-tbody>
                                            <b-tr>
                                                <b-td colspan="10" class="text-center">{{ translate('nothingFound') }}</b-td>
                                            </b-tr>
                                        </b-tbody>
                                    </template>
                                    
                                    <template v-else>
                                        <b-tbody>
                                            <b-tr
                                                v-for="(log, index) in logs"
                                                :key="log.id"
                                            >
                                                <b-td>{{ (currentPage - 1) * perPage + index + 1 }}</b-td>
                                                <b-td>
                                                    <b-badge variant="info" class="cursor-pointer" @click="filterByUsername(log.username)">
                                                        {{ log.username }}
                                                    </b-badge>
                                                </b-td>
                                                <b-td>{{ log.userFullName || '-' }}</b-td>
                                                <b-td>
                                                    <b-badge :variant="getActionVariant(log.action)">
                                                        {{ log.action }}
                                                    </b-badge>
                                                </b-td>
                                                <b-td>
                                                    <b-badge variant="secondary" class="cursor-pointer" @click="filterByTableName(log.tableName)">
                                                        {{ log.tableName }}
                                                    </b-badge>
                                                </b-td>
                                                <b-td>{{ log.tableId || '-' }}</b-td>
                                                <b-td class="text-truncate" style="max-width: 200px;">
                                                    {{ log.description || '-' }}
                                                    <b-button 
                                                        v-if="log.description && log.description.length > 50"
                                                        size="sm" 
                                                        variant="link" 
                                                        @click="showFullDescription(log)"
                                                        class="p-0 ml-1">
                                                        {{ translate('more') }}
                                                    </b-button>
                                                </b-td>
                                                <b-td>{{ log.ipAddress || '-' }}</b-td>
                                                <b-td>{{ formatDate(log.createdAt) }}</b-td>
                                                <b-td>
                                                    <b-button size="sm" variant="primary" @click="viewDetails(log)" :title="translate('viewDetails')">
                                                        <b-icon icon="eye"></b-icon>
                                                    </b-button>
                                                </b-td>
                                            </b-tr>
                                        </b-tbody>
                                    </template>
                                </template>
                            </b-table-simple>
                        </div>

                        <!-- Pagination -->
                        <div class="pagination-container">
                            <b-row class="mt-3 align-items-center justify-content-between">
                                <b-col class="d-flex align-items-center">
                                    <label class="mr-2 mb-0">{{ translate('recordsPerPage') }}</label>
                                    <b-form-select
                                        class="custom-dropdown"
                                        v-model="perPage"
                                        :options="pageOptions"
                                        @change="changePerPage"
                                    >
                                    </b-form-select>
                                    <span class="ml-3 text-muted" v-if="totalItems">
                                        Total: {{ totalItems }} records
                                    </span>
                                </b-col>
                                <b-col class="d-flex justify-content-end">
                                    <b-pagination
                                        v-model="currentPage"
                                        :per-page="perPage"
                                        :total-rows="totalItems"
                                        @page-click="changePage"
                                        align="right"
                                    />
                                </b-col>
                            </b-row>
                        </div>
                    </card>
                </div>
            </div>
        </div>

        <!-- Detail Modal -->
        <b-modal 
            id="auditLogDetailModal" 
            size="lg"
            title="Audit Log Details"
            hide-footer
            centered
        >
            <div v-if="selectedLog">
                <b-col md="6">
                    <div class="detail-card">
                        <label class="detail-label">Audit Log Id:</label>
                        <p class="detail-value">{{ selectedLog.id }}</p>
                    </div>
                </b-col>
                
                <div class="section-header">Action By Info</div>
                <b-row class="mb-3">
                    <b-col md="6">
                        <div class="detail-card">
                            <label class="detail-label">User Id:</label>
                            <p class="detail-value">{{ selectedLog.userId || '-' }}</p>
                        </div>
                    </b-col>
                </b-row>

                <b-row class="mb-3">
                    <b-col md="6">
                        <div class="detail-card">
                            <label class="detail-label">User Name:</label>
                            <p class="detail-value">{{ selectedLog.username || '-' }}</p>
                        </div>
                    </b-col>
                    <b-col md="6">
                        <div class="detail-card">
                            <label class="detail-label">User Full Name:</label>
                            <p class="detail-value">{{ selectedLog.userFullName || '-' }}</p>
                        </div>
                    </b-col>
                </b-row>
                <b-row class="mb-3">
                    <b-col md="6">
                        <div class="detail-card">
                            <label class="detail-label">Company:</label>
                            <p class="detail-value">{{ selectedLog.companyName || selectedLog.company || selectedLog.superUserCompany || '-' }}</p>
                        </div>
                    </b-col>
                </b-row>

                <div class="section-header">Action Info</div>
                <b-row class="mb-3">
                    <b-col md="6">
                        <div class="detail-card">
                            <label class="detail-label">Action:</label>
                            <p class="detail-value">
                                <b-badge :variant="getActionVariant(selectedLog.action)">
                                    {{ selectedLog.action || '-' }}
                                </b-badge>
                            </p>
                        </div>
                    </b-col>
                    <b-col md="6">
                        <div class="detail-card">
                            <label class="detail-label">Table Name:</label>
                            <p class="detail-value">{{ selectedLog.tableName || '-' }}</p>
                        </div>
                    </b-col>
                </b-row>

                <b-row class="mb-3">
                    <b-col md="6">
                        <div class="detail-card">
                            <label class="detail-label">Record ID:</label>
                            <p class="detail-value">{{ selectedLog.tableId || '-' }}</p>
                        </div>
                    </b-col>
                    <b-col md="6">
                        <div class="detail-card">
                            <label class="detail-label">Endpoint:</label>
                            <p class="detail-value">{{ selectedLog.endpoint || '-' }}</p>
                        </div>
                    </b-col>
                </b-row>

                <b-row class="mb-3">
                    <b-col md="12">
                        <div class="detail-card">
                            <label class="detail-label">IP Address:</label>
                            <p class="detail-value">{{ selectedLog.ipAddress || '-' }}</p>
                        </div>
                    </b-col>
                </b-row>

                <div class="section-header">History</div>
                <b-row class="mb-3">
                    <b-col md="12">
                        <div class="detail-card">
                            <label class="detail-label">Description:</label>
                            <p class="detail-value">{{ selectedLog.description || '-' }}</p>
                        </div>
                    </b-col>
                </b-row>

                <b-row class="mb-3">
                    <b-col md="12">
                        <div class="detail-card">
                            <label class="detail-label">Old Value:</label>
                            <pre class="detail-json">{{ formatJSON(selectedLog.oldValue) }}</pre>
                        </div>
                    </b-col>
                </b-row>

                <b-row class="mb-3">
                    <b-col md="12">
                        <div class="detail-card">
                            <label class="detail-label">New Value:</label>
                            <pre class="detail-json">{{ formatJSON(selectedLog.newValue) }}</pre>
                        </div>
                    </b-col>
                </b-row>

                <div class="section-header">Metadata</div>
                <b-row class="mb-3">
                    <b-col md="12">
                        <div class="detail-card">
                            <label class="detail-label">Created At:</label>
                            <p class="detail-value">{{ formatDateTime(selectedLog.createdAt) }}</p>
                        </div>
                    </b-col>
                </b-row>
            </div>
        </b-modal>

        <!-- Full Description Modal -->
        <b-modal 
            id="fullDescriptionModal" 
            size="lg"
            title="Full Description"
            hide-footer
            centered
        >
            <p class="description-text">{{ fullDescription }}</p>
        </b-modal>
    </div>
</template>

<script>
    import Card from 'src/components/Cards/Card.vue';
    import jsPDF from 'jspdf';
    import autoTable from 'jspdf-autotable';
    import { setLanguage, translate } from 'src/i18n.js';

    export default {
        components: {
            Card
        },
        data() {
            return {
                showFilters: false,
                currentPage: 1,
                perPage: 10,
                totalItems: 0,
                totalPages: 0,
                pageOptions: [
                    { value: 10, text: 10 },
                    { value: 25, text: 25 },
                    { value: 50, text: 50 },
                    { value: 100, text: 100 }
                ],
                logs: [],
                users: [],
                superAdmins: [],
                spinner: true,
                selectedLog: null,
                fullDescription: '',
                
                // Filters
                filters: {
                    userId: null,
                    superUserId: null,
                    username: '',
                    action: null,
                    tableName: '',
                    tableId: '',
                    fromDate: '',
                    toDate: ''
                },
                
                actionOptions: [
                    { text: translate('all'), value: null },
                    { text: 'CREATE_ATTEMPT', value: 'CREATE_ATTEMPT' },
                    { text: 'CREATE', value: 'CREATE' },
                    { text: 'UPDATE_ATTEMPT', value: 'UPDATE_ATTEMPT' },
                    { text: 'UPDATE', value: 'UPDATE' },
                    { text: 'DELETE_ATTEMPT', value: 'DELETE_ATTEMPT' },
                    { text: 'DELETE', value: 'DELETE' },
                    { text: 'LOGIN_ATTEMPT', value: 'LOGIN_ATTEMPT' },
                    { text: 'LOGIN', value: 'LOGIN' },
                    { text: 'ACCOUNT_LOCKED', value: 'ACCOUNT_LOCKED' },
                    { text: 'UNLOCK_USER', value: 'UNLOCK_USER' },
                    { text: 'LOCK_USER', value: 'LOCK_USER' },
                    { text: 'PASSWORD_RESET_ATTEMPT', value: 'PASSWORD_RESET_ATTEMPT' },
                    { text: 'PASSWORD_RESET', value: 'PASSWORD_RESET' },
                    { text: 'PASSWORD_CHANGE_ATTEMPT', value: 'PASSWORD_CHANGE_ATTEMPT' },
                    { text: 'PASSWORD_CHANGE', value: 'PASSWORD_CHANGE' },
                    { text: 'PAYMENT', value: 'PAYMENT' }
                ],
                tableNameOptions: [
                    { text: translate('all'), value: null },
                    { text: 'ekub', value: 'ekub' },
                    { text: 'ekub_joiner', value: 'ekub_joiner' },
                    { text: 'ekub_linking', value: 'ekub_linking' },
                    { text: 'payment_history', value: 'payment_history' },
                    { text: 'user_list', value: 'user_list' },
                    { text: 'role', value: 'role' },
                    { text: 'module', value: 'module' },
                    { text: 'permission', value: 'permission' },
                    { text: 'ekub_taked_ekub_group', value: 'ekub_taked_ekub_group' },
                    { text: 'ekub_taking_member', value: 'ekub_taking_member' }
                ]
            };
        },
        computed: {
            userOptions() {
                return [
                    { value: null, text: translate('all') },
                    ...this.users.map(user => ({
                        value: user.id,
                        text: `${user.fullName || ''}${user.username ? ` (${user.username})` : ''}`
                    }))
                ];
            },
            superAdminOptions() {
                return [
                    { value: null, text: translate('all') },
                    ...this.superAdmins.map(user => ({
                        value: user.id,
                        text: user.companyName ? user.companyName : user.fullName
                    }))
                ];
            }
        },
        async mounted() {
            const results = await Promise.allSettled([this.fetchUserLists(), this.fetchAuditLogs()]);
            results.forEach((result) => {
                if (result.status === 'rejected') {
                    console.error('Initial load failed:', result.reason);
                }
            });
        },
        methods: {
            translate,
            changeLanguage(lang) {
                setLanguage(lang);
                this.$forceUpdate();
            },
            
            // Fetch audit logs with pagination from backend
            async fetchAuditLogs() {
                this.spinner = true;
                try {
                    const payload = {
                        userId: this.filters.userId ? Number(this.filters.userId) : null,
                        superUserId: this.filters.superUserId ? Number(this.filters.superUserId) : null,
                        username: this.filters.username && this.filters.username.trim() ? this.filters.username.trim() : null,
                        action: this.filters.action || null,
                        tableName: this.filters.tableName && this.filters.tableName.trim() ? this.filters.tableName.trim() : null,
                        tableId: this.filters.tableId && this.filters.tableId.trim() ? this.filters.tableId.trim() : null,
                        fromDate: this.filters.fromDate || null,
                        toDate: this.filters.toDate || null,
                        page: this.currentPage - 1,
                        size: this.perPage
                    };

                    const response = await this.$store.dispatch('getAllAuditLogs', payload);
                    
                    if (response && response.data) {
                        // Handle paginated response
                        if (response.data.data) {
                            this.logs = response.data.data;
                            this.totalItems = response.data.totalItems || 0;
                            this.totalPages = response.data.totalPages || 0;
                        } else {
                            // Fallback for non-paginated response
                            this.logs = response.data;
                            this.totalItems = this.logs.length;
                            this.totalPages = Math.ceil(this.totalItems / this.perPage);
                        }
                    }
                } catch (error) {
                    console.error('Error fetching audit logs:', error);
                    const status = (error && error.status) || (error && error.response && error.response.status) || null;
                    
                    if (status === 401) {
                        try {
                            await this.$store.dispatch('renewAccessToken');
                            const payload = {
                                userId: this.filters.userId ? Number(this.filters.userId) : null,
                                superUserId: this.filters.superUserId ? Number(this.filters.superUserId) : null,
                                username: this.filters.username && this.filters.username.trim() ? this.filters.username.trim() : null,
                                action: this.filters.action || null,
                                tableName: this.filters.tableName && this.filters.tableName.trim() ? this.filters.tableName.trim() : null,
                                tableId: this.filters.tableId && this.filters.tableId.trim() ? this.filters.tableId.trim() : null,
                                fromDate: this.filters.fromDate || null,
                                toDate: this.filters.toDate || null,
                                page: this.currentPage - 1,
                                size: this.perPage
                            };
                            const retry = await this.$store.dispatch('getAllAuditLogs', payload);
                            if (retry && retry.data) {
                                if (retry.data.data) {
                                    this.logs = retry.data.data;
                                    this.totalItems = retry.data.totalItems || 0;
                                    this.totalPages = retry.data.totalPages || 0;
                                } else {
                                    this.logs = retry.data;
                                    this.totalItems = this.logs.length;
                                    this.totalPages = Math.ceil(this.totalItems / this.perPage);
                                }
                                this.spinner = false;
                                return;
                            }
                        } catch (e) {
                            console.error('Token renew or retry failed:', e);
                        }
                    }

                    const message = (error && error.data) ? (error.data.message || error.data) : 
                                   ((error && error.response && error.response.data && error.response.data.message) ? 
                                   error.response.data.message : 'Failed to load audit logs');
                    this.$notify({
                        title: 'Error',
                        text: message,
                        type: 'danger'
                    });
                    this.logs = [];
                    this.totalItems = 0;
                    this.totalPages = 0;
                } finally {
                    this.spinner = false;
                }
            },
            
            // Apply filters and reset to first page
            applyFilters() {
                this.currentPage = 1;
                this.fetchAuditLogs();
            },
            
            // Clear all filters
            clearFilters() {
                this.filters = {
                    userId: null,
                    superUserId: null,
                    username: '',
                    action: null,
                    tableName: '',
                    tableId: '',
                    fromDate: '',
                    toDate: ''
                };
                this.currentPage = 1;
                this.fetchAuditLogs();
            },
            
            // Quick filter by username
            filterByUsername(username) {
                this.filters.username = username;
                this.applyFilters();
                this.showFilters = true;
            },
            
            // Quick filter by table name
            filterByTableName(tableName) {
                this.filters.tableName = tableName;
                this.applyFilters();
                this.showFilters = true;
            },
            
            // Refresh data
            refreshData() {
                this.clearFilters();
            },
            
            // View full details in modal
            viewDetails(log) {
                this.selectedLog = log;
                this.$bvModal.show('auditLogDetailModal');
            },
            
            // Show full description
            showFullDescription(log) {
                this.fullDescription = log.description;
                this.$bvModal.show('fullDescriptionModal');
            },
            
            // Get action badge variant
            getActionVariant(action) {
                const variants = {
                    'CREATE_ATTEMPT': 'warning',
                    'CREATE': 'success',
                    'UPDATE_ATTEMPT': 'warning',
                    'UPDATE': 'warning',
                    'DELETE_ATTEMPT': 'warning',
                    'DELETE': 'danger',
                    'LOGIN_ATTEMPT': 'warning',
                    'LOGIN': 'primary',
                    'ACCOUNT_LOCKED': 'danger',
                    'UNLOCK_USER': 'success',
                    'LOCK_USER': 'danger',
                    'PASSWORD_RESET_ATTEMPT': 'warning',
                    'PASSWORD_RESET': 'info',
                    'PASSWORD_CHANGE_ATTEMPT': 'warning',
                    'PASSWORD_CHANGE': 'info',
                    'PAYMENT': 'success'
                };
                return variants[action] || 'light';
            },
            
            // Format date for display
            formatDate(dateString) {
                if (!dateString) return '-';
                const date = new Date(dateString);
                return date.toLocaleDateString();
            },
            
            // Format datetime for display
            formatDateTime(dateString) {
                if (!dateString) return '-';
                const date = new Date(dateString);
                return date.toLocaleString();
            },
            
            // Format JSON for display
            formatJSON(jsonString) {
                if (!jsonString) return '-';
                try {
                    const parsed = typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString;
                    return JSON.stringify(parsed, null, 2);
                } catch (e) {
                    return jsonString;
                }
            },
            
            // Export to Excel (CSV)
            async exportToExcel() {
                // If we're on a filtered view, use current logs
                // If we need all data, we might need to fetch all
                const exportData = this.logs;
                
                if (exportData.length === 0) {
                    this.$notify({
                        title: 'Warning',
                        text: 'No data to export',
                        type: 'warning'
                    });
                    return;
                }
                
                const headers = ['No', 'Performed By', 'Company', 'Action', 'Table Name', 'Record ID', 'Description', 'Old Value', 'New Value', 'Endpoint', 'IP Address', 'Created At'];

                const escape = (val) => {
                    if (val === null || val === undefined) return '""';
                    const s = (typeof val === 'string') ? val : (typeof val === 'object' ? JSON.stringify(val) : String(val));
                    return '"' + s.replace(/"/g, '""').replace(/\r?\n/g, ' ') + '"';
                };

                const rows = exportData.map((log, index) => {
                    const performedBy = `${log.userFullName || ''}${log.username ? ` (${log.username})` : ''}`.trim() || '-';
                    const company = log.companyName || log.company || '-';
                    const oldVal = this.formatJSON(log.oldValue);
                    const newVal = this.formatJSON(log.newValue);

                    return [
                        index + 1,
                        performedBy,
                        company,
                        log.action || '-',
                        log.tableName || '-',
                        log.tableId || '-',
                        log.description || '-',
                        oldVal || '-',
                        newVal || '-',
                        log.endpoint || '-',
                        log.ipAddress || '-',
                        this.formatDateTime(log.createdAt)
                    ].map(escape).join(',');
                });

                const csv = `\ufeff${headers.map(h => '"' + h + '"').join(',')}\n${rows.join('\n')}`;
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement('a');
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', 'audit_logs.csv');
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                this.$notify({
                    title: 'Success',
                    text: 'Export completed successfully',
                    type: 'success'
                });
            },
            
            // Export to PDF
            async exportToPDF() {
                const exportData = this.logs;
                
                if (exportData.length === 0) {
                    this.$notify({
                        title: 'Warning',
                        text: 'No data to export',
                        type: 'warning'
                    });
                    return;
                }
                
                try {
                    const pdf = new jsPDF({ orientation: 'landscape' });
                    pdf.setFontSize(10);
                    const headers = [
                        'No', 'Performed By', 'Company', 'Action', 'Table Name', 
                        'Record ID', 'Description', 'Old Value', 'New Value', 'IP Address', 'Created At'
                    ];

                    const rows = exportData.map((log, index) => {
                        const performedBy = `${log.userFullName || ''}${log.username ? ` (${log.username})` : ''}`.trim() || '-';
                        const company = log.companyName || log.company || '-';
                        const oldVal = this.formatJSON(log.oldValue);
                        const newVal = this.formatJSON(log.newValue);

                        return [
                            index + 1,
                            performedBy,
                            company,
                            log.action || '-',
                            log.tableName || '-',
                            log.tableId || '-',
                            (log.description || '-').substring(0, 50),
                            (oldVal || '-').substring(0, 100),
                            (newVal || '-').substring(0, 100),
                            log.ipAddress || '-',
                            this.formatDateTime(log.createdAt)
                        ];
                    });
                    
                    autoTable(pdf, {
                        head: [headers],
                        body: rows,
                        styles: { fontSize: 8 },
                        headStyles: {
                            fillColor: [37, 91, 48],
                            textColor: [240, 206, 13],
                            fontSize: 8
                        }
                    });
                    
                    pdf.save('audit_logs.pdf');
                    
                    this.$notify({
                        title: 'Success',
                        text: 'PDF exported successfully',
                        type: 'success'
                    });
                } catch (error) {
                    console.error('PDF Export Error:', error);
                    this.$notify({
                        title: 'Error',
                        text: 'Failed to export PDF',
                        type: 'danger'
                    });
                }
            },
            
            // Pagination methods
            changePage(event, page) {
                this.currentPage = page;
                this.fetchAuditLogs();
            },
            
            changePerPage(event) {
                this.currentPage = 1;
                this.fetchAuditLogs();
            },
            
            toggleFilters() {
                this.showFilters = !this.showFilters;
            },

            async fetchUserLists() {
                if (this.users.length > 0) {
                    return;
                }
                try {
                    const response = await this.$store.dispatch('getAllUsers');
                    if (response && response.data) {
                        const users = response.data || [];
                        this.users = users.map(user => ({
                            id: user.id,
                            fullName: user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
                            username: user.username || '',
                            companyName: user.companyName || (user.company && (user.company.name || user.company.companyName)) || '',
                            roleName: (typeof user.role === 'string') ? user.role : (user.role && user.role.name ? user.role.name : '')
                        }));
                        this.superAdmins = this.users.filter(u => u.roleName === 'SUPER_ADMIN');
                    }
                } catch (error) {
                    console.error('Error fetching users:', error);
                    this.$notify({
                        title: 'Error',
                        text: 'Failed to load user list for filters',
                        type: 'danger'
                    });
                }
            }
        }
    };
</script>

<style scoped>
.text-warning {
    color: rgba(240, 206, 13, 0.937) !important;
}

.table-container {
    display: flex;
    flex-direction: column;
    max-height: calc(80vh - 0px);
    overflow-y: auto;
    overflow-x: auto;
}

.pagination-container {
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
}

.sticky-header {
    position: sticky;
    top: 0;
    background-color: #fff;
    z-index: 10;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.cursor-pointer {
    cursor: pointer;
}

.detail-card {
    background: #f8f9fa;
    padding: 10px;
    border-radius: 5px;
    border-left: 3px solid #f0ce0d;
}

.detail-label {
    font-weight: bold;
    color: #255b30;
    display: block;
    margin-bottom: 5px;
    font-size: 0.85rem;
    text-transform: uppercase;
}

.detail-value {
    margin: 0;
    word-break: break-word;
    color: #333;
}

.detail-json {
    background: #2d2d2d;
    color: #f8f8f2;
    padding: 10px;
    border-radius: 5px;
    overflow-x: auto;
    font-size: 0.8rem;
    margin: 0;
    max-height: 200px;
}

.section-header {
    font-size: 1rem;
    font-weight: 700;
    margin: 1rem 0 0.5rem;
    color: #255b30;
    border-bottom: 1px solid rgba(37, 91, 48, 0.2);
    padding-bottom: 0.4rem;
}

.description-text {
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 14px;
    line-height: 1.6;
}

/* Mobile adjustments */
@media (max-width: 768px) {
    .pagination-container {
        margin-top: 20px;
        flex-wrap: wrap;
    }

    .table-container {
        max-height: 50vh;
    }

    .b-button-toolbar {
        flex-wrap: wrap;
        justify-content: flex-start;
    }

    .b-button {
        padding: 8px;
        margin: 5px;
    }

    .detail-card {
        margin-bottom: 10px;
    }
}
</style>